import type { IAdViewManaged } from "@finn-no/adview-provider-frontend-types";
import { i18n } from "@lingui/core";
import { WIcon } from "@mobility-item-page/client";
import { ElementType, Intent } from "@mobility-item-page/utilities/client";
import { Button } from "@warp-ds/react/components/button";
import { Pill } from "@warp-ds/react/components/pill";
import { useEffect, useRef, useState } from "react";
import { BrandLogo } from "../../assets/BrandLogo.tsx";
import { useAdViewAd } from "../../context/AdViewProviderContext.tsx";
import { useAppConfig } from "../../context/AppConfigProviderContext.tsx";
import { FeatureIds } from "../../types/unleash.ts";
import { getReview } from "../../utils/expertReviewMapper.ts";
import usePulse from "../../utils/pulse-tracking/usePulse.ts";
import { Icon } from "./Icon.tsx";

interface ExpertReviewCardProps {
  carModelTaxonomy: IAdViewManaged;
  year: number;
  modelSpec: string;
  fuelType: IAdViewManaged;
}

export const ExpertReviewCard = ({ carModelTaxonomy, year, modelSpec, fuelType }: ExpertReviewCardProps) => {
  const { brand, featureToggles } = useAppConfig();
  const { adViewType } = useAdViewAd();
  const { trackClick, trackElementView } = usePulse();
  const cardRef = useRef<HTMLDivElement>(null);
  const [clickedReview, setClickedReview] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<null | "no" | "done">(null);

  const isValidAdViewType = ["car-new", "car-leasing", "car-used"].includes(adViewType);
  const review = isValidAdViewType ? getReview(brand, carModelTaxonomy, year, modelSpec, fuelType) : null;

  useEffect(() => {
    const el = cardRef.current;
    if (!el || !review) return;

    const { title, reviewUrl } = review;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackElementView({
            name: "View expert-review",
            objectId: "MO_pv31",
            elementType: ElementType.Link,
            intent: Intent.View,
            target: {
              type: "Article",
              name: title,
              id: reviewUrl,
            },
          });
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [review, trackElementView]);

  if (!featureToggles[FeatureIds.ENABLE_EXPERT_CAR_REVIEW]) {
    return null;
  }

  if (!isValidAdViewType || !review) {
    return null;
  }

  const { thumbnailUrl, title, reviewUrl } = review;

  const badgeText = i18n.t({
    id: "expertReviewCard.badge.label",
    message: "Expert review",
  });

  const onClickHandler = () => {
    setClickedReview(true);
    // Tracking event: MO_ev102
    trackClick({
      name: "Read expert-review",
      objectId: "MO_ev102",
      elementType: ElementType.Link,
      intent: Intent.View,
      target: {
        type: "Article",
        name: title,
        id: reviewUrl,
      },
    });
  };

  const onReviewFeedback = (reason: string) => {
    // Tracking event: MO_ev103
    trackClick({
      name: "Expert-review feedback",
      objectId: "MO_ev103",
      elementType: ElementType.Button,
      intent: Intent.Rate,
      objectExtras: {
        name: reason,
      },
    });
  };

  const feedbackList: { id: string; reason: string }[] = [
    {
      id: "wrongModel",
      reason: i18n.t({
        id: "expertReviewCard.feedback.reason.wrongModel",
        message: "Review is for a different model or trim",
        comment: "Negative feedback reason: review doesn't match the car model",
      }),
    },
    {
      id: "wrongYear",
      reason: i18n.t({
        id: "expertReviewCard.feedback.reason.wrongYear",
        message: "Review covers a different model year",
        comment: "Negative feedback reason: review is for a different year",
      }),
    },
    {
      id: "outdated",
      reason: i18n.t({
        id: "expertReviewCard.feedback.reason.outdated",
        message: "Review content is outdated",
        comment: "Negative feedback reason: review is no longer up to date",
      }),
    },
    {
      id: "notUseful",
      reason: i18n.t({
        id: "expertReviewCard.feedback.reason.notUseful",
        message: "Review didn't help my buying decision",
        comment: "Negative feedback reason: review was not useful for purchase decision",
      }),
    },
  ];

  return (
    <div ref={cardRef} className="relative mt-40">
      <div className="flex flex-row overflow-hidden rounded-t-8">
        <img src={thumbnailUrl} alt={title} className="w-[33%] md:w-[189px] h-auto object-cover shrink-0" />
        <div
          className={`flex flex-col justify-center gap-4 p-16 border-2 s-border ${clickedReview ? "rounded-tr-8" : "rounded-r-8"} border-l-0 flex-1`}
        >
          <div className="flex items-center gap-8 text-s s-text-subtle">
            <BrandLogo height={18} brandOverride={brand === "dba" ? "bilbasen" : brand} />
            {badgeText}
          </div>
          <h2 className="t4 m-0 line-clamp-2">{title}</h2>
          <a
            href={reviewUrl}
            target="_blank"
            onClick={onClickHandler}
            onAuxClick={(e) => {
              if (e.button === 1) onClickHandler();
            }}
            className="s-text-link flex items-center gap-4"
            rel="noopener"
          >
            {i18n.t({
              id: "expertReviewCard.link.label.car",
              message: "Read our car review",
              comment: "Link text to read the expert review of the car",
            })}
            <WIcon name="LinkExternal" />
          </a>
        </div>
      </div>
      {clickedReview && (
        <div className="flex items-center p-8 border-2 border-t-0 s-border rounded-b-8 gap-8">
          {feedback === null && (
            <>
              <span className="s-text-subtle text-s">
                {i18n.t({
                  id: "expertReviewCard.feedback.label",
                  message: "Was this expert review relevant to you?",
                  comment: "Label text for feedback on the expert review card",
                })}
              </span>
              <Button
                link
                className="s-text-link text-s flex items-center"
                onClick={() => {
                  setFeedback("done");
                  onReviewFeedback("like");
                }}
              >
                <Icon name="Like" />
              </Button>
              {" / "}
              <Button
                link
                className="s-text-link text-s flex items-center"
                onClick={() => {
                  setFeedback("no");
                  onReviewFeedback("dislike");
                }}
              >
                <Icon name="Dislike" />
              </Button>
            </>
          )}
          {feedback === "done" && (
            <span className="s-text-subtle text-s">
              {i18n.t({
                id: "expertReviewCard.feedback.thankYou.label",
                message: "Thank you for your feedback!",
                comment: "Label text for feedback on the expert review card after user has given feedback",
              })}
            </span>
          )}
          {feedback === "no" && (
            <div className="flex flex-col items-center gap-8">
              <span className="s-text-subtle text-s">
                {i18n.t({
                  id: "expertReviewCard.feedback.whynot.label",
                  message: "What did you find not relevant about the review?",
                  comment: "Label text for feedback on the expert review card after user has given negative feedback",
                })}
              </span>
              <div className="flex flex-wrap gap-8">
                {feedbackList.map((item) => (
                  <Pill
                    key={item.reason}
                    label={item.reason}
                    suggestion
                    onClick={() => {
                      setFeedback("done");
                      onReviewFeedback(item.id);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
